import {
  Category as CategoryIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Email as SendEmailIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Fade,
  IconButton,
  InputAdornment,
  LinearProgress,
  Skeleton,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { parseISO } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { CommunicationTypeEnum } from "../../../store/services/communicationService";
import { MyCustomerApiResponse, useLazySearchCustomerQuery } from "../../../store/services/customerService";
import {
  addSavedFilter,
  clearAllFilters,
  SavedFilter,
  setActionFilter,
  setActivePredefinedFilter,
  setActivityFilter,
  setConsumptionFilter,
  setContactFilter,
  setCurrentPage,
  setDeliveryEndFilter,
  setDeliveryEndReasonFilter,
  setDirectDebitFilter,
  setInvoiceDeliveryFilter,
  setJourneyTypeFilter,
  setRowsPerPage,
  setSearchQuery,
  setSegmentFilter,
  setSegmentImportanceFilter,
  setSegmentSizeFilter,
} from "../../../store/slices/customerFilterSlice";
import { CreateSuspectDialog } from "../../create-suspect/CreateSuspect";
import { formatOrgNumber, isValidOrgNumber } from "../../create-suspect/orgNumber";
import UpdateCustomerSegmentDialog from "../../dialogs/UpdateCustomerSegmentDialog";
import CustomerCard from "./CustomerCard";
import CustomerCardRow from "./CustomerCardRow";
import CustomerCardRowHeader from "./CustomerCardRowHeader";
import {
  CardFieldConfig,
  ColumnConfig,
  CustomerDataField,
  DEFAULT_CARD_FIELDS,
  DEFAULT_CUSTOMER_COLUMNS,
} from "./CustomerColumnConfig";
import { getMostRecentLogin, isWithinDays, matchesDeliveryEndFilter } from "./dateUtils";
import { NO_DELIVERY_END_REASON } from "./deliveryEndReasons";
import FilterButton from "./FilterButton";
import FilterPopover from "./FilterPopover";
import SaveFilterDialog from "./SaveFilterDialog";

interface CustomerListBaseProps {
  customers: MyCustomerApiResponse[];
  isLoading: boolean;
  isFetching: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  renderContextMenu?: (customer: MyCustomerApiResponse, event: React.MouseEvent) => void;
  columnConfig?: ColumnConfig[];
  cardFieldConfig?: CardFieldConfig[];
}

// Sort types
type SortField = CustomerDataField;
type SortDirection = "asc" | "desc";

// Custom hook for debounce
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const CustomerListBase: React.FC<CustomerListBaseProps> = ({
  customers,
  isLoading,
  isFetching,
  error,
  renderContextMenu,
  columnConfig = DEFAULT_CUSTOMER_COLUMNS,
  cardFieldConfig = DEFAULT_CARD_FIELDS,
}) => {
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);

  const [showExternalResults, setShowExternalResults] = useState(false);
  const [showCreateSuspectDialog, setShowCreateSuspectDialog] = useState(false);
  const [isSegmentDialogOpen, setIsSegmentDialogOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLElement | null>(
    null
  );

  // Sorting state
  const [sortField, setSortField] = useState<SortField>(CustomerDataField.NAME);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // External search state

  const [
    searchCustomerExternal,
    {
      data: externalSearchData,
      isFetching: isSearchingExternal,
      error: externalSearchError,
    },
  ] = useLazySearchCustomerQuery();
  const externalCustomers = externalSearchData?.CustomerReturnModel || [];

  // Use Redux state
  const dispatch = useAppDispatch();
  const {
    segmentFilter,
    segmentImportanceFilter,
    segmentSizeFilter,
    activityFilter,
    journeyTypeFilter,
    consumptionFilter,
    actionFilter,
    contactFilter,
    invoiceDeliveryFilter,
    directDebitFilter,
    deliveryEndFilter,
    deliveryEndReasonFilter,
    searchQuery,
    activePredefinedFilter,
    savedFilters,
    currentPage,
    rowsPerPage,
  } = useAppSelector((state) => state.customerFilter);
  const debouncedSearchQuery = useDebounce(searchQuery, 700);

  // Snabbval: skapa suspect direkt när söket på ett organisationsnummer inte ger träff
  const searchQueryDigits = searchQuery.replace(/\D/g, "");
  const searchLooksLikeOrgNumber = isValidOrgNumber(searchQueryDigits);
  // Use Redux state for pagination instead of local state
  const page = currentPage;
  const handleSetPage = (newPage: number) => dispatch(setCurrentPage(newPage));
  const handleSetRowsPerPage = (newRowsPerPage: number) =>
    dispatch(setRowsPerPage(newRowsPerPage));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  // Sort customers first, before filtering
  const sortedCustomers = useMemo(() => {
    const sortValue = (customer: MyCustomerApiResponse) => {
      switch (sortField) {
        case CustomerDataField.NAME:
          return customer.Name;
        case CustomerDataField.CUSTOMER_ID:
          return customer.CustomerId;
        case CustomerDataField.SEGMENT:
          return customer.CustomerSegmentName || "";
        case CustomerDataField.JOURNEY_TYPE:
          return customer.B2bJourneyType || "";
        case CustomerDataField.CONSUMPTION:
          return customer.ActiveFVolume || 0;
        case CustomerDataField.LAST_CONTACT:
          return customer.LastAction?.ActionDate
            ? parseISO(customer.LastAction.ActionDate).getTime()
            : 0;
        case CustomerDataField.LAST_LOGIN: {
          const login = getMostRecentLogin(customer);
          return login ? parseISO(login).getTime() : 0;
        }
        case CustomerDataField.NEXT_ACTION:
          return customer.NextAction?.ActionDate
            ? parseISO(customer.NextAction.ActionDate).getTime()
            : 0;
        case CustomerDataField.SALES_REP:
          return customer.SalesResponisble || "";
        case CustomerDataField.POSTAL_CITY:
          return customer.PostalCity || "";
        default:
          return customer.Name;
      }
    };

    return [...customers].sort((a, b) => {
      const valueA = sortValue(a);
      const valueB = sortValue(b);

      if (valueA === valueB) return 0;

      const compareResult = valueA < valueB ? -1 : 1;
      return sortDirection === "asc" ? compareResult : -compareResult;
    });
  }, [customers, sortField, sortDirection]);

  // Apply filters and search to sorted customers
  const filteredCustomers = useMemo(() => {
    // Special case for "A-kunder med nästa aktivitet"
    const hasNextActionFilter =
      activePredefinedFilter === "a-priority-next-action";

    // Phone search: only when the query looks like a phone number, compared
    // digits-only so formatting (spaces, dashes, +46) doesn't matter.
    // Values are stored as +46..., so 46/0046 is rewritten to national 0-prefix
    // to let e.g. "041712810" match "+4641712810".
    const toNationalDigits = (value: string) =>
      value.replace(/\D/g, "").replace(/^(00)?46/, "0");
    const phoneQuery =
      /^[\d\s\-+()]+$/.test(searchQuery.trim()) &&
      searchQuery.replace(/\D/g, "").length >= 3
        ? toNationalDigits(searchQuery)
        : null;

    return sortedCustomers.filter((customer) => {
      // Text search
      const matchesSearch =
        searchQuery === "" ||
        customer.Name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.CustomerId?.toLowerCase().includes(
          searchQuery.toLowerCase()
        ) ||
        customer.PostalCity?.toLowerCase().includes(
          searchQuery.toLowerCase()
        ) ||
        (customer.CustomerSegmentName &&
          customer.CustomerSegmentName.toLowerCase().includes(
            searchQuery.toLowerCase()
          )) ||
        (customer.B2bJourneyType &&
          customer.B2bJourneyType.toLowerCase().includes(
            searchQuery.toLowerCase()
          )) ||
        (phoneQuery !== null &&
          customer.Communications.some(
            (comm) =>
              (comm.Type === CommunicationTypeEnum.Telefon ||
                comm.Type === CommunicationTypeEnum.Mobiltelefon) &&
              comm.Value !== undefined &&
              toNationalDigits(comm.Value).includes(phoneQuery)
          ));

      if (!matchesSearch) return false;

      // Special filter for next action
      if (hasNextActionFilter && !customer.NextAction?.ActionDate) {
        return false;
      }

      // Action filters
      if (
        actionFilter === "has-next-action" &&
        !customer.NextAction?.ActionDate
      ) {
        return false;
      }

      if (
        actionFilter === "no-next-action" &&
        customer.NextAction?.ActionDate
      ) {
        return false;
      }

      if (
        actionFilter === "has-last-action" &&
        !customer.LastAction?.ActionDate
      ) {
        return false;
      }

      if (
        actionFilter === "no-last-action" &&
        customer.LastAction?.ActionDate
      ) {
        return false;
      }

      if (actionFilter === "last-action-6m") {
        if (!customer.LastAction?.ActionDate) return false;
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        if (new Date(customer.LastAction.ActionDate) < sixMonthsAgo) return false;
      }

      if (actionFilter === "last-action-3m") {
        if (!customer.LastAction?.ActionDate) return false;
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        if (new Date(customer.LastAction.ActionDate) < threeMonthsAgo) return false;
      }

      // Segment (exact match) filter
      if (segmentFilter !== null) {
        // Empty string means "no segment"
        if (segmentFilter === "") {
          if (customer.CustomerSegmentName) {
            return false;
          }
        } else if (customer.CustomerSegmentName !== segmentFilter) {
          return false;
        }
      }

      // Segment importance level filter (first character)
      if (
        segmentImportanceFilter !== null &&
        (!customer.CustomerSegmentName ||
          customer.CustomerSegmentName.charAt(0) !== segmentImportanceFilter)
      ) {
        return false;
      }

      // Segment size filter (second character)
      if (
        segmentSizeFilter !== null &&
        (!customer.CustomerSegmentName ||
          customer.CustomerSegmentName.charAt(1) !== segmentSizeFilter)
      ) {
        return false;
      }

      // Journey Type filter
      if (
        journeyTypeFilter !== null &&
        customer.B2bJourneyType !== journeyTypeFilter
      ) {
        return false;
      }

      // Consumption filter
      if (consumptionFilter !== "all") {
        if (customer.ActiveFVolume === null) return false;
        if (
          typeof consumptionFilter === "object" &&
          consumptionFilter !== null
        ) {
          // Interval filter
          if (
            customer.ActiveFVolume < consumptionFilter.min ||
            customer.ActiveFVolume > consumptionFilter.max
          ) {
            return false;
          }
        } else {
          // Legacy string filter
          switch (consumptionFilter) {
            case "low":
              if (customer.ActiveFVolume >= 100000) return false;
              break;
            case "medium":
              if (
                customer.ActiveFVolume < 100000 ||
                customer.ActiveFVolume > 500000
              )
                return false;
              break;
            case "high":
              if (customer.ActiveFVolume <= 500000) return false;
              break;
          }
        }
      }

      // Activity filter
      if (activityFilter === "logged-in" && !customer.WebLogins.length) {
        return false;
      }

      if (activityFilter === "logged-in-7d") {
        const recentLogin = getMostRecentLogin(customer);
        if (!recentLogin || !isWithinDays(recentLogin, 7)) return false;
      }

      if (activityFilter === "logged-in-30d") {
        const recentLogin = getMostRecentLogin(customer);
        if (!recentLogin || !isWithinDays(recentLogin, 30)) return false;
      }

      if (activityFilter === "logged-in-90d") {
        const recentLogin = getMostRecentLogin(customer);
        if (!recentLogin || !isWithinDays(recentLogin, 90)) return false;
      }

      if (activityFilter === "no-login" && customer.WebLogins.length > 0) {
        return false;
      }

      if (activityFilter === "no-contact" && customer.LastAction?.ActionDate) {
        return false;
      }

      // Contact filter
      const hasContact = customer.Communications.find(x => x.ContactPersonNo !== null) !== undefined;

      if (contactFilter === "has-contact" && !hasContact) {
        return false;
      }

      if (contactFilter === "no-contact" && hasContact) {
        return false;
      }

      // Invoice delivery filter
      if (invoiceDeliveryFilter !== null && customer.InvoiceDelivery !== invoiceDeliveryFilter) {
        return false;
      }

      // Direct debit filter
      if (directDebitFilter === "yes" && !customer.DirectDebit) {
        return false;
      }
      if (directDebitFilter === "no" && customer.DirectDebit) {
        return false;
      }

      // Delivery end date filter
      if (!matchesDeliveryEndFilter(customer, deliveryEndFilter)) {
        return false;
      }

      // Delivery end reason filter (matchar på kod, inte No som skiljer per bolag)
      if (
        deliveryEndReasonFilter !== null &&
        (customer.CustomerDeliveryEndReasonId ?? NO_DELIVERY_END_REASON) !==
          deliveryEndReasonFilter
      ) {
        return false;
      }

      return true;
    });
  }, [
    sortedCustomers, // Now using sortedCustomers instead of customers
    searchQuery,
    segmentFilter,
    segmentImportanceFilter,
    segmentSizeFilter,
    journeyTypeFilter,
    consumptionFilter,
    activityFilter,
    actionFilter,
    contactFilter,
    invoiceDeliveryFilter,
    directDebitFilter,
    deliveryEndFilter,
    deliveryEndReasonFilter,
    activePredefinedFilter,
  ]);

  // Run external search when debounced search term changes and no local matches
  useEffect(() => {
    if (debouncedSearchQuery.length >= 3 && filteredCustomers.length === 0) {
      searchCustomerExternal({
        CustomerName: debouncedSearchQuery,
        NewCustomerId: debouncedSearchQuery,
        CustomerEntityOrgId: debouncedSearchQuery,
        CustomerEMaill: debouncedSearchQuery,
        PhoneNumber: debouncedSearchQuery,
      });
      setShowExternalResults(true);
    } else {
      setShowExternalResults(false);
    }
  }, [debouncedSearchQuery, filteredCustomers.length, searchCustomerExternal]);

  const displayedCustomers = useMemo(() => {
    return filteredCustomers.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredCustomers, page, rowsPerPage]);

  // Handle sort request
  const handleRequestSort = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === "asc";
    setSortDirection(isAsc ? "desc" : "asc");
    setSortField(field);
  };

  // Handle sort direction change
  const handleSortDirectionChange = (direction: SortDirection) => {
    setSortDirection(direction);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    handleSetPage(newPage);
    // Scroll to top on page change for better mobile experience
    window.scrollTo(0, 0);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleSetRowsPerPage(parseInt(event.target.value, 10));
  };

  const goToCustomerDetails = (customerId: number) => {
    navigate(`/customer/${customerId}`);
  };

  const handleSaveFilter = (name: string) => {
    const currentFilterState: SavedFilter["filters"] = {
      segmentFilter,
      segmentImportanceFilter,
      segmentSizeFilter,
      activityFilter,
      journeyTypeFilter,
      consumptionFilter,
      searchQuery,
      actionFilter: actionFilter,
      contactFilter: contactFilter,
      invoiceDeliveryFilter,
      directDebitFilter,
      deliveryEndFilter,
      deliveryEndReasonFilter,
    };

    const newSavedFilter: SavedFilter = {
      id: uuidv4(),
      name: name,
      filters: currentFilterState,
    };

    dispatch(addSavedFilter(newSavedFilter));
  };

  // Get names of existing saved filters to avoid duplicates
  const existingFilterNames = useMemo(
    () => savedFilters.map((f) => f.name.toLowerCase()),
    [savedFilters]
  );

  // Get unique segments for filter options
  const uniqueSegments = useMemo(() => {
    const segments = customers
      .filter((customer) => customer.CustomerSegmentName)
      .map((customer) => customer.CustomerSegmentName)
      .filter((segment): segment is string => Boolean(segment));

    return Array.from(new Set(segments)).sort();
  }, [customers]);

  // Get unique journey types for filter options
  const uniqueJourneyTypes = useMemo(() => {
    const journeyTypes = customers
      .filter((customer) => customer.B2bJourneyType)
      .map((customer) => customer.B2bJourneyType)
      .filter((journeyType): journeyType is string => Boolean(journeyType));

    return Array.from(new Set(journeyTypes)).sort();
  }, [customers]);

  // Get unique delivery end years for filter options (newest first)
  const uniqueDeliveryEndYears = useMemo(() => {
    const years = customers
      .map((customer) => customer.CustomerDeliveryEndDate?.slice(0, 4))
      .filter((year): year is string => Boolean(year))
      .map(Number);

    return Array.from(new Set(years)).sort((a, b) => b - a);
  }, [customers]);

  // Function to clear all filters with Redux
  const handleClearAllFilters = () => {
    dispatch(clearAllFilters());
    handleSetPage(0);
  };

  // Custom context menu handler
  const handleContextMenu = (
    customer: MyCustomerApiResponse,
    event: React.MouseEvent
  ) => {
    if (renderContextMenu) {
      renderContextMenu(customer, event);
    }
  };

  // Handle selection changes
  const handleSelectionChange = (newSelection: number[]) => {
    setSelectedRows(newSelection);
  };

  // Handle bulk segment update
  const handleOpenBulkSegmentDialog = () => {
    if (selectedRows.length > 0) {
      setIsSegmentDialogOpen(true);
    }
  };

  const handleCloseBulkSegmentDialog = () => {
    setIsSegmentDialogOpen(false);
  };

  // Toggle select all customers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(displayedCustomers.map((c) => c.Id));
    } else {
      setSelectedRows([]);
    }
  };

  // Handle bulk actions for selected rows
  const handleBulkAction = (action: string) => {
    console.log(`Bulk action "${action}" on customers:`, selectedRows);
    setSelectedRows([]);
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        {[1, 2, 3].map((item) => (
          <Skeleton
            key={item}
            variant="rectangular"
            height={isMobile ? 120 : 60}
            sx={{ mb: 2 }}
          />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Det gick inte att hämta kunddata. Försök igen senare.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Enhanced loading indicator shown when isFetching is true */}
      <Fade in={isFetching} timeout={300}>
        <Box>
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              borderTopLeftRadius: theme.shape.borderRadius,
              borderTopRightRadius: theme.shape.borderRadius,
              zIndex: 2,
              "& .MuiLinearProgress-bar": {
                backgroundColor: theme.palette.primary.main,
              },
              backgroundColor: theme.palette.primary.light + "40",
            }}
          />
          {/* Optional subtle overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
        </Box>
      </Fade>

      {/* Top row with title and buttons */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: "row",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left side - Title and count */}
        <Box>
          <Typography variant="subtitle1" color="text.secondary">
            {filteredCustomers.length}{" "}
            {filteredCustomers.length === 1 ? "kund" : "kunder"}{customers.length !== filteredCustomers.length ? ` av ${customers.length}` : ''}
          </Typography>
        </Box>

        {/* Right side - Action buttons */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <FilterButton
            segmentFilter={segmentFilter}
            setSegmentFilter={(filter) => {
              dispatch(setSegmentFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            segmentImportanceFilter={segmentImportanceFilter}
            setSegmentImportanceFilter={(filter) => {
              dispatch(setSegmentImportanceFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            segmentSizeFilter={segmentSizeFilter}
            setSegmentSizeFilter={(filter) => {
              dispatch(setSegmentSizeFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            activityFilter={activityFilter}
            setActivityFilter={(filter) => {
              dispatch(setActivityFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            actionFilter={actionFilter}
            setActionFilter={(filter) => {
              dispatch(setActionFilter(filter));
              handleSetPage(0);
            }}
            contactFilter={contactFilter}
            setContactFilter={(filter) => {
              dispatch(setContactFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            journeyTypeFilter={journeyTypeFilter}
            setJourneyTypeFilter={(filter) => {
              dispatch(setJourneyTypeFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            consumptionFilter={consumptionFilter}
            setConsumptionFilter={(filter) => {
              dispatch(setConsumptionFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            invoiceDeliveryFilter={invoiceDeliveryFilter}
            setInvoiceDeliveryFilter={(filter) => {
              dispatch(setInvoiceDeliveryFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            directDebitFilter={directDebitFilter}
            setDirectDebitFilter={(filter) => {
              dispatch(setDirectDebitFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            deliveryEndFilter={deliveryEndFilter}
            setDeliveryEndFilter={(filter) => {
              dispatch(setDeliveryEndFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            deliveryEndReasonFilter={deliveryEndReasonFilter}
            setDeliveryEndReasonFilter={(filter) => {
              dispatch(setDeliveryEndReasonFilter(filter));
              if (activePredefinedFilter)
                dispatch(setActivePredefinedFilter(null));
              handleSetPage(0);
            }}
            onOpenFilter={(event) => setFilterAnchorEl(event.currentTarget)}
            onClearAllFilters={handleClearAllFilters}
          />

          {/*    <Button
            variant="outlined"
            color="primary"
            startIcon={<SearchIcon />}
            endIcon={showSearch ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowSearch(!showSearch)}
            sx={{
              whiteSpace: "nowrap",
              transition: theme.transitions.create([
                "background-color",
                "box-shadow",
                "border-color",
              ]),
              "&:hover": {
                backgroundColor: theme.palette.primary.light + "20",
              },
            }}
          >
            {showSearch ? "Dölj sökning" : "Visa sökning"}
          </Button> */}
        </Box>
      </Box>

      {/* Filter popover */}
      <FilterPopover
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
        segmentFilter={segmentFilter}
        setSegmentFilter={(filter) => {
          dispatch(setSegmentFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        segmentImportanceFilter={segmentImportanceFilter}
        setSegmentImportanceFilter={(filter) => {
          dispatch(setSegmentImportanceFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        segmentSizeFilter={segmentSizeFilter}
        setSegmentSizeFilter={(filter) => {
          dispatch(setSegmentSizeFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        activityFilter={activityFilter}
        setActivityFilter={(filter) => {
          dispatch(setActivityFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        actionFilter={actionFilter}
        setActionFilter={(filter) => {
          dispatch(setActionFilter(filter));
          handleSetPage(0);
        }}
        contactFilter={contactFilter}
        setContactFilter={(filter) => {
          dispatch(setContactFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        uniqueSegments={uniqueSegments}
        journeyTypeFilter={journeyTypeFilter}
        setJourneyTypeFilter={(filter) => {
          dispatch(setJourneyTypeFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        uniqueJourneyTypes={uniqueJourneyTypes}
        consumptionFilter={consumptionFilter}
        setConsumptionFilter={(filter) => {
          dispatch(setConsumptionFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        invoiceDeliveryFilter={invoiceDeliveryFilter}
        setInvoiceDeliveryFilter={(filter) => {
          dispatch(setInvoiceDeliveryFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        directDebitFilter={directDebitFilter}
        setDirectDebitFilter={(filter) => {
          dispatch(setDirectDebitFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        deliveryEndFilter={deliveryEndFilter}
        setDeliveryEndFilter={(filter) => {
          dispatch(setDeliveryEndFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        uniqueDeliveryEndYears={uniqueDeliveryEndYears}
        deliveryEndReasonFilter={deliveryEndReasonFilter}
        setDeliveryEndReasonFilter={(filter) => {
          dispatch(setDeliveryEndReasonFilter(filter));
          if (activePredefinedFilter) dispatch(setActivePredefinedFilter(null));
          handleSetPage(0);
        }}
        onClearAll={handleClearAllFilters}
      />

      {/* Expandable search field */}

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Sök på kundnummer, namn, ort, segment, kundtyp eller telefon..."
        value={searchQuery}
        onChange={(e) => {
          dispatch(setSearchQuery(e.target.value));
          if (e.target.value.length === 0) {
            setShowExternalResults(false);
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchQuery ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => dispatch(setSearchQuery(""))}
                edge="end"
                aria-label="clear search"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        autoFocus={true}
      />

      {/* Status bar with page info and loading indicator */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Visar {displayedCustomers.length} av {filteredCustomers.length}{" "}
          resultat
        </Typography>

        {/* Enhanced loading indicator */}
        {isFetching && (
          <Chip
            icon={<CircularProgress size={16} color="primary" />}
            label={isMobile ? "Uppdaterar..." : "Hämtar data..."}
            size="small"
            variant="outlined"
            sx={{
              ml: 1,
              animation: "pulse 1.5s ease-in-out infinite",
              "@keyframes pulse": {
                "0%": {
                  opacity: 1,
                },
                "50%": {
                  opacity: 0.7,
                },
                "100%": {
                  opacity: 1,
                },
              },
            }}
          />
        )}
      </Box>

      {/* External search results section */}
      {showExternalResults &&
        searchQuery.length >= 3 &&
        filteredCustomers.length === 0 && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: "info.lighter",
              borderRadius: 2,
              border: `1px solid ${theme.palette.info.light}`,
            }}
          >
            <Typography
              variant="subtitle2"
              color="info.main"
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              Söker i hela kunddatabasen...
            </Typography>

            {isSearchingExternal && (
              <Box display="flex" alignItems="center" py={2}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">
                  Söker efter "{searchQuery}"...
                </Typography>
              </Box>
            )}

            {externalSearchError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Kunde inte söka. Försök igen senare.
              </Alert>
            )}

            {!isSearchingExternal && externalCustomers.length > 0 && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Hittade {externalCustomers.length} kund
                  {externalCustomers.length > 1 ? "er" : ""} utanför din lista:
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                  {externalCustomers.map((customer) => (
                    <Box
                      key={customer.CustomerNo}
                      sx={{
                        p: 2,
                        mb: 1,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: "action.hover",
                          borderColor: "primary.main",
                        },
                      }}
                      onClick={() =>
                        navigate(`/customer/${customer.CustomerNo}`)
                      }
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="start"
                      >
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight="medium">
                            {customer.CustomerName}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Kundnr: {customer.CustomerId}
                          </Typography>

                          {customer.Address && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {customer.Address}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}

            {!isSearchingExternal && externalCustomers.length === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Inga kunder hittades för "{searchQuery}"
                </Typography>
                {searchLooksLikeOrgNumber && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setShowCreateSuspectDialog(true)}
                    sx={{ mt: 1.5 }}
                  >
                    Skapa suspekt för {formatOrgNumber(searchQueryDigits)}
                  </Button>
                )}
              </Box>
            )}
          </Box>
        )}

      <CreateSuspectDialog
        open={showCreateSuspectDialog}
        onClose={() => setShowCreateSuspectDialog(false)}
        defaultOrgNumber={searchQueryDigits}
      />

      {isMobile ? (
        <Box>
          {displayedCustomers.length > 0 ? (
            displayedCustomers.map((customer) => (
              <CustomerCard
                key={customer.Id}
                customer={customer}
                onClick={() => goToCustomerDetails(customer.Id)}
                onContextMenu={(event) => handleContextMenu(customer, event)}
                fieldConfig={cardFieldConfig.filter((field) => field.visible)}
              />
            ))
          ) : (
            <Alert severity="info" sx={{ my: 2 }}>
              Inga kunder matchar dina filterval. Justera filtren eller rensa
              sökfältet för att se fler kunder.
            </Alert>
          )}
        </Box>
      ) : (
        <>
          {/* Bulk action buttons (visible when rows are selected) */}
          {selectedRows.length > 0 && (
            <Box
              sx={{
                mb: 2,
                display: "flex",
                gap: 1,
                alignItems: "center",
                bgcolor: "background.paper",
                p: 1,
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              <Typography variant="body2" sx={{ mr: 1 }}>
                {selectedRows.length}{" "}
                {selectedRows.length === 1 ? "kund vald" : "kunder valda"}
              </Typography>
              <IconButton
                size="small"
                title="Skicka e-post"
                onClick={() => handleBulkAction("email")}
              >
                <SendEmailIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                title="Redigera"
                onClick={() => handleBulkAction("edit")}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <Tooltip title="Ändra segment för valda">
                <IconButton size="small" onClick={handleOpenBulkSegmentDialog}>
                  <CategoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box sx={{ flex: 1 }} />
              <Chip
                label="Avbryt markering"
                size="small"
                onClick={() => setSelectedRows([])}
                sx={{ ml: "auto" }}
              />
            </Box>
          )}

          {displayedCustomers.length > 0 ? (
            <>
              <CustomerCardRowHeader
                columnConfig={columnConfig}
                sortField={sortField}
                sortDirection={sortDirection}
                onRequestSort={handleRequestSort}
                onSortDirectionChange={handleSortDirectionChange}
                allSelected={
                  selectedRows.length > 0 &&
                  selectedRows.length === displayedCustomers.length &&
                  displayedCustomers.every((c) => selectedRows.includes(c.Id))
                }
                indeterminate={
                  selectedRows.length > 0 &&
                  selectedRows.length < displayedCustomers.length
                }
                onSelectAll={handleSelectAll}
                totalCount={displayedCustomers.length}
                selectedCount={
                  selectedRows.filter((id) =>
                    displayedCustomers.some((c) => c.Id === id)
                  ).length
                }
              />
              <CustomerCardRow
                customers={displayedCustomers}
                onCustomerClick={goToCustomerDetails}
                onContextMenu={renderContextMenu}
                selectedRows={selectedRows}
                onSelectionChange={handleSelectionChange}
                onBulkSegmentUpdate={handleOpenBulkSegmentDialog}
              />
            </>
          ) : (
            <Alert severity="info" sx={{ my: 2 }}>
              Inga kunder matchar dina filterval. Justera filtren eller rensa
              sökfältet för att se fler kunder.
            </Alert>
          )}
        </>
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredCustomers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Rader per sida"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} av ${count}`
        }
      />
      <SaveFilterDialog
        open={isSaveDialogOpen}
        onClose={() => setIsSaveDialogOpen(false)}
        onSave={handleSaveFilter}
        existingNames={existingFilterNames}
      />

      {selectedRows.length > 0 && (
        <UpdateCustomerSegmentDialog
          open={isSegmentDialogOpen}
          onClose={handleCloseBulkSegmentDialog}
          customerIds={selectedRows}
        />
      )}
    </Box>
  );
};

export default CustomerListBase;
